// src/pages/Profile.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useAuth } from "../AuthContext";
import { db, storage } from "../firebase";
import {
  doc,
  getDoc,
  setDoc,
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

export default function Profile() {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile] = useState({
    fullName: "",
    phone: "",
    address: "",
    profilePhotoUrl: "",
  });

  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState("");
  const [profileSavedMsg, setProfileSavedMsg] = useState("");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersError, setOrdersError] = useState("");

  // ---------- helpers ----------
  const userDocRef =
    currentUser && doc(db, "users", currentUser.uid);

  const loadProfile = async () => {
    if (!currentUser || !userDocRef) return;
    setProfileLoading(true);
    setProfileError("");
    setProfileSavedMsg("");

    try {
      const snap = await getDoc(userDocRef);
      if (snap.exists()) {
        const data = snap.data();
        setProfile({
          fullName:
            data.fullName ||
            currentUser.displayName ||
            currentUser.email?.split("@")[0] ||
            "",
          phone: data.phone || "",
          address: data.address || "",
          profilePhotoUrl: data.profilePhotoUrl || "",
        });
      } else {
        // first-time profile
        setProfile({
          fullName:
            currentUser.displayName ||
            currentUser.email?.split("@")[0] ||
            "",
          phone: "",
          address: "",
          profilePhotoUrl: "",
        });
      }
    } catch (err) {
      console.error("Failed to load profile:", err);
      setProfileError("Failed to load your profile.");
    } finally {
      setProfileLoading(false);
    }
  };

  const loadOrders = async () => {
    if (!currentUser) return;
    setOrdersLoading(true);
    setOrdersError("");

    try {
      const q = query(
        collection(db, "orders"),
        where("userId", "==", currentUser.uid),
        orderBy("createdAt", "desc"),
        limit(5)
      );

      const snap = await getDocs(q);
      const list = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));
      setOrders(list);
    } catch (err) {
      console.error("Failed to load orders:", err);
      setOrdersError("Failed to load your orders.");
    } finally {
      setOrdersLoading(false);
    }
  };

  useEffect(() => {
    if (!currentUser) return;
    loadProfile();
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (err) {
      alert("Failed to log out: " + err.message);
    }
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!currentUser || !userDocRef) return;

    setProfileError("");
    setProfileSavedMsg("");

    try {
      await setDoc(
        userDocRef,
        {
          fullName: profile.fullName,
          phone: profile.phone,
          address: profile.address,
          profilePhotoUrl: profile.profilePhotoUrl || "",
          email: currentUser.email || "",
          updatedAt: new Date(),
        },
        { merge: true }
      );
      setProfileSavedMsg("Profile saved successfully!");
    } catch (err) {
      console.error("Failed to save profile:", err);
      setProfileError("Failed to save your profile.");
    }
  };

  const handleProfilePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !currentUser) return;

    setUploadingPhoto(true);
    setProfileError("");
    setProfileSavedMsg("");

    try {
      // upload to Storage
      const fileRef = ref(
        storage,
        `profilePhotos/${currentUser.uid}/${file.name}`
      );
      await uploadBytes(fileRef, file);
      const url = await getDownloadURL(fileRef);

      // save URL in user doc
      if (userDocRef) {
        await setDoc(
          userDocRef,
          {
            profilePhotoUrl: url,
            updatedAt: new Date(),
          },
          { merge: true }
        );
      }

      setProfile((prev) => ({
        ...prev,
        profilePhotoUrl: url,
      }));
      setProfileSavedMsg("Profile photo updated!");
    } catch (err) {
      console.error("Failed to upload photo:", err);
      setProfileError("Failed to upload profile photo.");
    } finally {
      setUploadingPhoto(false);
      // reset input so same file can be chosen again if needed
      e.target.value = "";
    }
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white">
        <p>You must be logged in to view your profile.</p>
      </div>
    );
  }

  const displayPhoto =
    profile.profilePhotoUrl || "/Profile.jpg";

  return (
    <div className="flex flex-col min-h-screen text-white bg-gradient-to-b from-gray-900 via-purple-900 to-black relative">
      <Navbar />

      <div
        className="absolute inset-0 bg-cover bg-center opacity-20 z-0"
        style={{ backgroundImage: 'url("/nails.jpeg")' }}
      />

      <main className="relative z-10 flex-grow flex items-start justify-center pt-24 px-6">
        <div className="w-full max-w-6xl flex flex-col md:flex-row gap-8">
          {/* Left: Profile box */}
          <div className="md:w-1/3 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-6 text-center">
            <div className="relative inline-block mb-4">
              <img
                src={displayPhoto}
                alt="Profile"
                className="w-32 h-32 rounded-full border-4 border-pink-400 object-cover"
              />
              <label className="absolute bottom-0 right-0 bg-pink-500 hover:bg-pink-600 text-xs px-2 py-1 rounded-full cursor-pointer">
                {uploadingPhoto ? "Uploading..." : "Change"}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleProfilePhotoChange}
                  className="hidden"
                  disabled={uploadingPhoto}
                />
              </label>
            </div>

            <h2 className="text-xl font-bold text-pink-400">
              {profile.fullName || currentUser.email}
            </h2>
            <p className="text-white/80 mb-4">
              {currentUser.email}
            </p>

            <button
              onClick={handleLogout}
              className="mt-4 px-6 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition"
            >
              Log Out
            </button>

            <p className="mt-4 text-xs text-white/60 break-all">
              User ID: {currentUser.uid}
            </p>
          </div>

          {/* Right: Personal info + Recent orders */}
          <div className="md:w-2/3 flex flex-col gap-8">
            {/* Personal info */}
            <form
              onSubmit={handleSaveProfile}
              className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-6"
            >
              <h3 className="text-lg font-semibold text-pink-400 mb-4">
                Personal Information
              </h3>

              <div className="space-y-4 text-white/80">
                <div>
                  <label className="block text-sm mb-1">
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={profile.fullName}
                    onChange={handleProfileChange}
                    className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-1">
                    Phone
                  </label>
                  <input
                    type="text"
                    name="phone"
                    value={profile.phone}
                    onChange={handleProfileChange}
                    className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-1">
                    Address
                  </label>
                  <textarea
                    name="address"
                    value={profile.address}
                    onChange={handleProfileChange}
                    rows={3}
                    className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white resize-none"
                  />
                </div>
              </div>

              {profileError && (
                <p className="mt-3 text-sm text-red-400">
                  {profileError}
                </p>
              )}
              {profileSavedMsg && (
                <p className="mt-3 text-sm text-green-400">
                  {profileSavedMsg}
                </p>
              )}

              <button
                type="submit"
                disabled={profileLoading}
                className="mt-4 px-6 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition disabled:opacity-50"
              >
                {profileLoading ? "Saving..." : "Save Changes"}
              </button>
            </form>

            {/* Recent Orders */}
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-pink-400 mb-4">
                Recent Orders
              </h3>

              {ordersLoading ? (
                <p className="text-white/70">
                  Loading your orders...
                </p>
              ) : ordersError ? (
                <p className="text-red-400 text-sm">
                  {ordersError}
                </p>
              ) : orders.length === 0 ? (
                <p className="text-white/60">
                  You haven&apos;t placed any orders yet.
                </p>
              ) : (
                <ul className="space-y-3">
                  {orders.map((order) => (
                    <li
                      key={order.id}
                      className="bg-black/30 rounded-lg p-4 flex justify-between items-start gap-4"
                    >
                      <div>
                        <p className="font-semibold">
                          Order #{order.id.slice(0, 6)}
                        </p>
                        <p className="text-xs text-white/60 mb-1">
                          {order.createdAt
                            ? new Date(
                                order.createdAt.seconds *
                                  1000
                              ).toLocaleString()
                            : ""}
                        </p>
                        {Array.isArray(order.items) && (
                          <ul className="text-sm text-white/80 list-disc ml-4">
                            {order.items.map((it, idx) => (
                              <li key={idx}>
                                {it.name} – $
                                {Number(it.price || 0).toFixed(
                                  2
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-sm">
                          <span className="text-white/60">
                            Status:
                          </span>{" "}
                          <span className="font-semibold text-green-300">
                            {order.status || "pending"}
                          </span>
                        </p>
                        <p className="text-sm">
                          <span className="text-white/60">
                            Total:
                          </span>{" "}
                          <span className="font-semibold">
                            $
                            {Number(
                              order.subtotal || 0
                            ).toFixed(2)}
                          </span>
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
